import { usePickerTarget } from '@/components/modals/usePickerTarget';
import type { ThunkDispatch } from 'redux-thunk';
import type { AnyAction } from 'redux';
import type { RootState } from '@/store/store';
import { addTypemarkPickerItems } from '@/store/actions/typemarkPicker';
import { existingPickerItems, type PickerItem } from '@/components/modals/classificationPicker';
import { Pencil, Plus, List as ListIcon } from 'lucide-react';
import React, { Suspense, useState } from 'react';
import { useDispatch } from 'react-redux';
import { SkeletonGroup } from '@/components/ui/Skeleton';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/Button';
import { ActionBar } from '@/components/ui/ActionBar';
import DataTable from '../../../../components/DataTable';
import PickValueWithClassificationModal from '../../../../components/modals/PickValueWithClassificationModal';
import {
  addProperty,
  removeProperty,
  removeTechnicalReportItem,
  addSolution,
  removeSolution,
  removeReferenceProduct,
  GET_TYPE_MARK_TEMPLATE_DETAIL_SUCCESS,
} from '@/store/actions/typemarkTemplate';
import { isEmptyArray, findProperty } from '../../../../utils/array';

import TypeMarkComponentTable from '../../../project/typemark/components/TypeMarkComponentTable';
import SolutionPropertyDefinitionModal from '../../solution/components/modals/SolutionPropertyDefinitionModal';
import ColorsModal from '../../../project/typemark/components/modals/ColorsModal';
import TypeMarkModal from '../../../project/typemark/components/modals/TypeMarkModal';
import SolutionPickerModal from '../../../../components/modals/SolutionPickerModal';
import { useMutation, useQueryClient } from 'react-query';
import { apiService } from '../../../../utils/api';

import TemplateCompositionLayersTable from './TemplateCompositionLayersTable';
import TypeMarkCompositionView from '../../../project/typemark/components/TypeMarkCompositionView';
import { useGetTemplateLayers } from '../hooks/api';

const detailData = ({ title, description }: any = {}) => [
  { name: 'Název', value: title },
  { name: 'Popis', value: description },
];

const prepareDataForComponentSet = (component_set) => {
  if (!component_set) return [];
  const result = [];
  component_set.forEach((component) => {
    const values = {
      title: component.title,
      id: component.id,
      solution: null,
    };
    if (component.solution_set.length < 1) {
      result.push({ ...values, rowSpan: 1 });
    }
    component.solution_set.forEach((solution, i) => {
      if (i === 0) {
        result.push({
          ...values,
          solution,
          rowSpan: component.solution_set.length,
        });
      } else {
        result.push({
          ...values,
          solution,
          rowSpan: 0,
        });
      }
    });
  });

  return result;
};

export default function TypeMarkTemplateDetailView({ activeItemId, data, permission }) {
  const pickerTarget = usePickerTarget([activeItemId], true);
  const [visibleAddPropertyDialog, setVisibleAddPropertyDialog] = useState(false);
  const [visibleAddPropertyValueDialog, setVisibleAddPropertyValueDialog] = useState(false);
  const [visibleAddPTechnicalReportItemDialog, setVisibleAddPTechnicalReportItemDialog] =
    useState(false);
  const [visibleBasicEditDialog, setVisibleBasicEditDialog] = useState(false);
  const [visibleAddComponent, setVisibleAddComponent] = useState(false);
  const [activeComponentId, setActiveComponentId] = useState(null);
  const [pickedSolutionId] = useState(null);
  const [visibleColorsDialog, setVisibleColorsDialog] = useState(false);
  const [propertyIdForSetValue, setPropertyIdForSetValue] = useState<any>(null);
  const [visibleAddPropertySetDialog, setVisibleAddPropertySetDialog] = useState(false);
  const [visibleAddTechnicalReportItemSetDialog, setVisibleAddTechnicalReportItemSetDialog] =
    useState(false);
  const [visibleAddReferenceProductDialog, setVisibleAddReferenceProductDialog] = useState(false);
  // const { instances } = useSelector((state) => state.typeMark);
  const queryClient = useQueryClient();
  const queryKey = 'instance-type-template';
  const {
    data: templateLayers = [],
    isLoading: isLoadingLayers,
    error: layersError,
  } = useGetTemplateLayers(activeItemId);

  const edit = useMutation({
    mutationFn: (values: any) => {
      return apiService.patch(`/api/v1/${queryKey}/${activeItemId}/`, {
        ...values,
      });
    },
    onSuccess: ({ data }) => {
      queryClient.invalidateQueries(queryKey);
      dispatch({
        type: GET_TYPE_MARK_TEMPLATE_DETAIL_SUCCESS,
        data: data,
      });
      setVisibleBasicEditDialog(false);
    },
  });

  const dispatch = useDispatch<ThunkDispatch<RootState, unknown, AnyAction>>();

  const filterDiffProperties = () => {
    const properties = data?.property_set;

    const filtered = properties.filter((property) => !property.property_definition.diff_flag);

    if (properties.length > filtered.length) {
      return filtered
        .map((item) => ({
          ...item.property_definition,
          title: `${item.property_definition.code} ${item.property_definition.title}`,
          value: item.value,
        }))
        .concat([{ title: 'Různé', value: '', diffMulti: true }]);
    }
    return filtered.map((item) => ({
      ...item.property_definition,
      title: `${item.property_definition.code} ${item.property_definition.title}`,
      value: item.value,
    }));
  };

  const filterDiffTechnicalReportItems = () => {
    const technicalReportItems = data?.technical_report_item_set;

    const filtered = technicalReportItems.filter(
      (technicalReportItem) => !technicalReportItem.diff_flag
    );

    if (technicalReportItems.length > filtered.length) {
      return filtered
        .map((item) => ({
          ...item,
          title: `${item.code} ${item.title}`,
          value: item.value,
        }))
        .concat([{ title: 'Různé', value: '', diffMulti: true }]);
    }
    return filtered.map((item) => ({
      ...item,
      title: `${item.code} ${item.title}`,
      value: item.value,
    }));
  };

  const handleAddPropertySet = (items: PickerItem[]) =>
    dispatch(addTypemarkPickerItems(pickerTarget, 'propertySets', items));

  const handleAddTechnicalReportItemSet = (items: PickerItem[]) =>
    dispatch(addTypemarkPickerItems(pickerTarget, 'technicalReportSets', items));

  const handleAddProperty = (items: PickerItem[]) =>
    dispatch(addTypemarkPickerItems(pickerTarget, 'properties', items));

  const handleRemoveProperty = (id) => {
    const selectedProperty = findProperty(id, data?.property_set) as any;

    dispatch(
      removeProperty(activeItemId, {
        classified_property_definition: selectedProperty.property_definition.classified_id,
      })
    );
  };

  const handleAddTechnicalReportItem = (items: PickerItem[]) =>
    dispatch(addTypemarkPickerItems(pickerTarget, 'technicalReports', items));

  const handleRemoveTechnicalReportItem = (id) => {
    dispatch(
      removeTechnicalReportItem(activeItemId, {
        classified_technical_report_item: id,
      })
    );
  };

  const handleAddReferenceProduct = (items: PickerItem[]) =>
    dispatch(addTypemarkPickerItems(pickerTarget, 'products', items));

  const handleRemoveReferenceProduct = (id) => {
    dispatch(
      removeReferenceProduct(activeItemId, {
        classified_product: id,
      })
    );
  };

  const handleAddPropertyValue = (selectedValue) => {
    const selectedProperty = findProperty(propertyIdForSetValue, data?.property_set) as any;

    if (!propertyIdForSetValue) return;

    dispatch(
      addProperty(activeItemId, {
        classified_property_definition: selectedProperty.property_definition.classified_id,
        value: selectedValue.value,
      })
    );
    setVisibleAddPropertyValueDialog(false);
    setPropertyIdForSetValue(null);
  };

  const handleCloseTypeMarkDialog = () => {
    setVisibleBasicEditDialog(false);
  };

  const handleSubmitTypeMarkDialog = ({ values }) => {
    edit.mutate(values);
  };

  const handleRemoveSolution = (classified_solution_id, component_set_id) => {
    dispatch(removeSolution(activeItemId, classified_solution_id, component_set_id));
  };

  const handleAddComponent = (pickedSolution) => {
    dispatch(addSolution(activeItemId, activeComponentId, pickedSolution?.id, undefined));
    setVisibleAddComponent(false);
  };

  const handleSubmitColorsDialog = (values) => {
    dispatch(addSolution(activeItemId, activeComponentId, pickedSolutionId, values.appearance));
    setVisibleColorsDialog(false);
  };

  return (
    <>
      <div className="flex-1 min-w-0 overflow-y-auto">
        <ActionBar
          left={<h3 className="text-sm font-semibold tracking-tight">{data?.title}</h3>}
          right={
            permission === 2 && data ? (
              <Button
                variant="ghost"
                size="icon-sm"
                className="text-neutral-300 hover:text-neutral-700"
                data-testid="template-detail-edit-btn"
                onClick={() => setVisibleBasicEditDialog(true)}
              >
                <Pencil size={12} />
              </Button>
            ) : undefined
          }
        />

        <div className="px-8 py-5">
          <div className="grid grid-cols-[1.3fr_1fr] gap-5 items-start">
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <div className="aspect-[4/3] flex items-center justify-center bg-neutral-25/40">
                  <TypeMarkCompositionView
                    externalLayers={templateLayers as any}
                    externalLoading={isLoadingLayers}
                    externalError={layersError}
                    width={480}
                    height={360}
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="min-w-0">
              <CardHeader className="px-4 py-0 h-9 flex-row items-center justify-between space-y-0">
                <CardTitle>Detail</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Suspense fallback={<SkeletonGroup />}>
                  <div className="px-4 py-2 divide-y divide-neutral-100">
                    {detailData(data || {}).map((item, i) => (
                      <div key={i} className="grid grid-cols-[90px_1fr] gap-2 py-2 items-baseline">
                        <div className="text-xs text-neutral-500 truncate">{item.name}:</div>
                        <div className="text-sm text-neutral-900 break-words min-w-0">
                          {item.value || '—'}
                        </div>
                      </div>
                    ))}
                  </div>
                </Suspense>
              </CardContent>
            </Card>
          </div>

          <TemplateCompositionLayersTable templateId={activeItemId} />

          <div className="flex flex-col">
            <div className="mt-6">
              <div className="flex items-center justify-between pt-3 pb-1 border-b border-neutral-150 mb-4">
                <span className="text-sm font-semibold tracking-tight">Komponenty</span>
              </div>
              {data && isEmptyArray(data.component_set) && (
                <TypeMarkComponentTable
                  columns={[
                    { title: 'Název', key: 'title' },
                    { title: 'Řešení', key: 'solution' },
                    { title: 'Vzhled', key: 'color' },
                    { title: 'id', key: 'solutionId' },
                    { title: 'id', key: 'componentId' },
                  ]}
                  data={prepareDataForComponentSet(data?.component_set)}
                  onDeleteSolution={handleRemoveSolution}
                  onAdd={(id) => {
                    setVisibleAddComponent(true);
                    setActiveComponentId(id);
                  }}
                  onDelete={() => {}}
                  isTemplate
                />
              )}
            </div>

            <div className="mt-6">
              <div className="flex items-center justify-between pt-3 pb-1 border-b border-neutral-150 mb-4">
                <span className="text-sm font-semibold tracking-tight">Vlastnosti</span>
                {permission === 2 && data && (
                  <div className="flex items-center gap-1">
                    <button
                      className="h-7 w-7 rounded flex items-center justify-center text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50 transition-colors"
                      data-testid="template-detail-add-property-btn"
                      onClick={() => setVisibleAddPropertyDialog(true)}
                    >
                      <Plus size={14} />
                    </button>
                    <button
                      className="h-7 w-7 rounded flex items-center justify-center text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50 transition-colors"
                      data-testid="template-detail-add-property-set-btn"
                      onClick={() => setVisibleAddPropertySetDialog(true)}
                    >
                      <ListIcon size={14} />
                    </button>
                  </div>
                )}
              </div>
              {data && isEmptyArray(data.property_set) && (
                <DataTable
                  columns={[
                    { title: 'Název', key: 'title' },
                    { title: 'Hodnota', key: 'value' },
                    { title: 'Popis', key: 'description' },
                    { title: 'Norma', key: 'biblios', list: true },
                    { title: 'id', key: 'id' },
                  ]}
                  data={filterDiffProperties()}
                  onDelete={handleRemoveProperty}
                  onEdit={(id) => {
                    setPropertyIdForSetValue(id);
                    setVisibleAddPropertyValueDialog(true);
                  }}
                />
              )}
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-between pt-3 pb-1 border-b border-neutral-150 mb-4">
                <span className="text-sm font-semibold tracking-tight">
                  Položky technické zprávy
                </span>
                {permission === 2 && data && (
                  <div className="flex items-center gap-1">
                    <button
                      className="h-7 w-7 rounded flex items-center justify-center text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50 transition-colors"
                      data-testid="template-detail-add-tech-report-btn"
                      onClick={() => setVisibleAddPTechnicalReportItemDialog(true)}
                    >
                      <Plus size={14} />
                    </button>
                    <button
                      className="h-7 w-7 rounded flex items-center justify-center text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50 transition-colors"
                      data-testid="template-detail-add-tech-report-set-btn"
                      onClick={() => setVisibleAddTechnicalReportItemSetDialog(true)}
                    >
                      <ListIcon size={14} />
                    </button>
                  </div>
                )}
              </div>
              {data && isEmptyArray(data.technical_report_item_set) && (
                <DataTable
                  columns={[
                    { title: 'Název', key: 'title' },
                    { title: 'Popis', key: 'description' },
                    { title: 'id', key: 'id' },
                  ]}
                  data={filterDiffTechnicalReportItems()}
                  onDelete={handleRemoveTechnicalReportItem}
                />
              )}
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-between pt-3 pb-1 border-b border-neutral-150 mb-4">
                <span className="text-sm font-semibold tracking-tight">Výrobky</span>
                {permission === 2 && data && (
                  <div className="flex items-center gap-1">
                    <button
                      className="h-7 w-7 rounded flex items-center justify-center text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50 transition-colors"
                      data-testid="template-detail-add-reference-product-btn"
                      onClick={() => setVisibleAddReferenceProductDialog(true)}
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                )}
              </div>
              {data && isEmptyArray(data.reference_product_set) && (
                <DataTable
                  columns={[
                    { title: 'Kód', key: 'code' },
                    { title: 'Název', key: 'title' },
                    { title: 'Popis', key: 'description' },
                    { title: 'id', key: 'id' },
                  ]}
                  data={data.reference_product_set || []}
                  onDelete={handleRemoveReferenceProduct}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <PickValueWithClassificationModal
        multiple
        sessionKey={pickerTarget.sessionKey}
        alreadySelectedValues={existingPickerItems(data, 'propertySets')}
        visible={visibleAddPropertySetDialog}
        url="left-panel-set-property-definition/"
        pickerUrl="set-property-definition/left-panel-picker/"
        onClose={() => setVisibleAddPropertySetDialog(false)}
        onSubmit={handleAddPropertySet}
      />
      <PickValueWithClassificationModal
        multiple
        sessionKey={pickerTarget.sessionKey}
        alreadySelectedValues={existingPickerItems(data, 'technicalReportSets')}
        visible={visibleAddTechnicalReportItemSetDialog}
        url="left-panel-set-technical-report-item/"
        pickerUrl="set-technical-report-item/left-panel-picker/"
        onClose={() => setVisibleAddTechnicalReportItemSetDialog(false)}
        onSubmit={handleAddTechnicalReportItemSet}
      />
      <PickValueWithClassificationModal
        multiple
        sessionKey={pickerTarget.sessionKey}
        alreadySelectedValues={existingPickerItems(data, 'properties')}
        visible={visibleAddPropertyDialog}
        url="left-panel-property-definition/"
        pickerUrl="property-definition/left-panel-picker/"
        onClose={() => setVisibleAddPropertyDialog(false)}
        onSubmit={handleAddProperty}
      />
      <PickValueWithClassificationModal
        multiple
        sessionKey={pickerTarget.sessionKey}
        alreadySelectedValues={existingPickerItems(data, 'technicalReports')}
        visible={visibleAddPTechnicalReportItemDialog}
        url="left-panel-technical-report-item/"
        showDetail
        pickerUrl="technical-report-item/left-panel-picker/"
        onClose={() => setVisibleAddPTechnicalReportItemDialog(false)}
        onSubmit={handleAddTechnicalReportItem}
      />
      <PickValueWithClassificationModal
        multiple
        sessionKey={pickerTarget.sessionKey}
        alreadySelectedValues={existingPickerItems(data, 'products')}
        visible={visibleAddReferenceProductDialog}
        url="left-panel-product/"
        pickerUrl="product/left-panel-picker/"
        onClose={() => setVisibleAddReferenceProductDialog(false)}
        onSubmit={handleAddReferenceProduct}
      />
      <SolutionPickerModal
        visible={visibleAddComponent}
        onClose={() => setVisibleAddComponent(false)}
        classificationId={activeComponentId}
        onSubmit={handleAddComponent}
      />
      <SolutionPropertyDefinitionModal
        propertyId={propertyIdForSetValue}
        visible={visibleAddPropertyValueDialog}
        formProps={(data?.property_set || []).find(
          (property) => property.property_definition.id === propertyIdForSetValue
        )}
        onClose={() => {
          setVisibleAddPropertyValueDialog(false);
          setPropertyIdForSetValue(null);
        }}
        onSubmit={handleAddPropertyValue}
      />
      <TypeMarkModal
        title="Základní info"
        isTemplate
        visible={visibleBasicEditDialog}
        formProps={data}
        onSubmit={handleSubmitTypeMarkDialog}
        onClose={handleCloseTypeMarkDialog}
      />
      {visibleColorsDialog && (
        <ColorsModal
          title="Vzhledy"
          visible={visibleColorsDialog}
          formProps={data}
          onSubmit={handleSubmitColorsDialog}
          onClose={() => setVisibleColorsDialog(false)}
        />
      )}
    </>
  );
}
